import { useState, useRef, useMemo } from "react";
import { useClickOutside } from "@/hooks/useClickOutside";
import { Input } from "@/components/ui/input";
import {
  Gamepad2, SlidersHorizontal, ChevronDown, ChevronUp,
  ArrowUp, ArrowDown, ArrowUpDown, X, PackageCheck, Star, GraduationCap,
} from "lucide-react";
import GameDetailModal from "@/components/app/GameDetailModal";

// ─── Constants ────────────────────────────────────────────────

export const SORT_OPTIONS = [
  { value: "name",         label: "Name",          defaultDir: "asc"  },
  { value: "bgg_rating",   label: "BGG Rating",    defaultDir: "desc" },
  { value: "player_count", label: "Player Count",  defaultDir: "asc"  },
  { value: "complexity",   label: "Complexity",    defaultDir: "asc"  },
  { value: "play_time",    label: "Play Time",     defaultDir: "asc"  },
];

export const PLAYER_COUNTS = ["1", "2", "3", "4", "5", "6", "7+"];

export const PLAY_TIMES = [
  { value: "lt15",    label: "< 15 min" },
  { value: "15-30",   label: "15–30 min" },
  { value: "30-45",   label: "30–45 min" },
  { value: "45-60",   label: "45–60 min" },
  { value: "60-90",   label: "60–90 min" },
  { value: "90-120",  label: "90–120 min" },
  { value: "gt120",   label: "120+ min" },
];

const DEFAULT_FILTERS = {
  playerCounts:  [],
  playTimes:     [],
  minRating:     "",
  maxRating:     "",
  minWeight:     "",
  maxWeight:     "",
  beingBrought:  false,
  mechanics:     [],
  categories:    [],
};

// Derive the top N most-common mechanics/categories across the game list
function extractTagOptions(games, key, limit = 10) {
  const map = new Map();
  games.forEach((g) => {
    (g[key] || []).forEach((item) => {
      if (!map.has(item.id)) map.set(item.id, { id: item.id, name: item.name, count: 0 });
      map.get(item.id).count++;
    });
  });
  return Array.from(map.values())
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .slice(0, limit);
}

// ─── Sort / Filter logic ──────────────────────────────────────

function applySort(games, sort, dir) {
  const m = dir === "asc" ? 1 : -1;
  return [...games].sort((a, b) => {
    const map = {
      name:         [a.title?.toLowerCase(),     b.title?.toLowerCase()],
      bgg_rating:   [a.bgg_rating,               b.bgg_rating],
      player_count: [a.min_players,              b.min_players],
      complexity:   [a.weight,                   b.weight],
      play_time:    [a.min_playtime,             b.min_playtime],
    };
    const [va, vb] = map[sort] ?? [null, null];
    // Nulls always sink to the bottom regardless of sort direction
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    return va > vb ? m : va < vb ? -m : 0;
  });
}

function applyFilters(games, f, mode) {
  return games.filter((g) => {
    if (f.playerCounts.length > 0) {
      const ok = f.playerCounts.some((pc) => {
        if (pc === "7+") return (g.max_players ?? 0) >= 7;
        const n = parseInt(pc, 10);
        return (g.min_players ?? 0) <= n && (g.max_players ?? 99) >= n;
      });
      if (!ok) return false;
    }
    if (f.playTimes.length > 0) {
      const t = g.max_playtime ?? 0;
      const ok = f.playTimes.some((pt) => {
        if (pt === "lt15")   return t > 0 && t < 15;
        if (pt === "15-30")  return t >= 15  && t <= 30;
        if (pt === "30-45")  return t >= 30  && t <= 45;
        if (pt === "45-60")  return t >= 45  && t <= 60;
        if (pt === "60-90")  return t >= 60  && t <= 90;
        if (pt === "90-120") return t >= 90  && t <= 120;
        if (pt === "gt120")  return t > 120;
        return false;
      });
      if (!ok) return false;
    }
    if (f.minRating && (g.bgg_rating == null || g.bgg_rating < parseFloat(f.minRating))) return false;
    if (f.maxRating && (g.bgg_rating == null || g.bgg_rating > parseFloat(f.maxRating))) return false;
    if (f.minWeight && (g.weight     == null || g.weight     < parseFloat(f.minWeight)))  return false;
    if (f.maxWeight && (g.weight     == null || g.weight     > parseFloat(f.maxWeight)))  return false;
    if (mode === "attendee"     && f.beingBrought    && !g.being_brought) return false;
    if (mode === "being_brought" && f.teacherAvailable && !g.has_teacher)  return false;
    if (f.mechanics.length > 0) {
      const ok = f.mechanics.some((id) => (g.mechanics || []).some((m) => m.id === id));
      if (!ok) return false;
    }
    if (f.categories.length > 0) {
      const ok = f.categories.some((id) => (g.categories || []).some((c) => c.id === id));
      if (!ok) return false;
    }
    return true;
  });
}

function countActiveFilters(f, mode) {
  return [
    f.playerCounts.length > 0,
    f.playTimes.length > 0,
    f.minRating !== "",
    f.maxRating !== "",
    f.minWeight !== "",
    f.maxWeight !== "",
    mode === "attendee"      && f.beingBrought,
    mode === "being_brought" && (f.teacherAvailable ?? false),
    f.mechanics.length > 0,
    f.categories.length > 0,
  ].filter(Boolean).length;
}

// ─── Main component ───────────────────────────────────────────

/**
 * BoardGameList — reusable game library display with sort, filter, and
 * optional event interactions.
 *
 * Props:
 *   games          array    — game objects (empty = skeleton)
 *   mode           string   — "library" | "attendee" | "mine"
 *   hasBgg         bool     — whether the current user has a BGG username
 *   settingsPath   string   — link for "connect BGG" CTA
 *   myOwnedIds     Set      — bgg_ids the current user owns (drives Bring vs WantToPlay button)
 *   bringIds       Set      — bgg_ids the user is currently marked as bringing
 *   wantToPlayIds  Set      — bgg_ids the user wants to play
 *   canTeachIds    Set      — bgg_ids where an attendee can teach
 *   onBring        fn(bgg_id)
 *   onWantToPlay   fn(bgg_id)
 */
export default function BoardGameList({
  games = [],
  mode = "library",
  hasBgg = true,
  settingsPath,
  myOwnedIds = new Set(),
  bringIds = new Set(),
  wantToPlayIds = new Set(),
  canTeachIds = new Set(),
  onBring,
  onWantToPlay,
}) {
  const [sort, setSort]         = useState("name");
  const [sortDir, setSortDir]   = useState("asc");
  const [filters, setFilters]   = useState(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const sortRef = useRef(null);
  useClickOutside(sortRef, () => setSortOpen(false));

  const activeCount      = countActiveFilters(filters, mode);
  const processed        = useMemo(
    () => applyFilters(applySort(games, sort, sortDir), filters, mode),
    [games, sort, sortDir, filters, mode]
  );
  const selectedSort     = SORT_OPTIONS.find((o) => o.value === sort);
  const mechanicOptions  = useMemo(() => extractTagOptions(games, "mechanics"), [games]);
  const categoryOptions  = useMemo(() => extractTagOptions(games, "categories"), [games]);

  function toggleCheck(key, value) {
    setFilters((f) => ({
      ...f,
      [key]: f[key].includes(value)
        ? f[key].filter((v) => v !== value)
        : [...f[key], value],
    }));
  }

  function clearFilters() { setFilters(DEFAULT_FILTERS); }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border">
        {games.length > 0 ? (
          <span className="text-xs text-muted-foreground">
            {processed.length} {processed.length === 1 ? "game" : "games"}
            {processed.length !== games.length && ` of ${games.length}`}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}

        <div className="flex items-center gap-2">
          {/* Sort controls */}
          <div ref={sortRef} className="relative flex items-center">
            <button
              onClick={() => setSortOpen((o) => !o)}
              className="flex items-center gap-1.5 text-xs font-medium border border-border rounded-l-md px-3 py-1.5 hover:bg-muted transition-colors"
            >
              <ArrowUpDown className="h-3.5 w-3.5" />
              {selectedSort?.label}
              <ChevronDown className={`h-3 w-3 transition-transform ${sortOpen ? "rotate-180" : ""}`} />
            </button>
            <button
              onClick={() => setSortDir((d) => d === "asc" ? "desc" : "asc")}
              title={sortDir === "asc" ? "Ascending — click to sort descending" : "Descending — click to sort ascending"}
              className="flex items-center border border-l-0 border-border rounded-r-md px-2 py-1.5 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            >
              {sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-card border border-border rounded-lg shadow-lg z-20 py-1 overflow-hidden">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => { setSort(opt.value); setSortDir(opt.defaultDir); setSortOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-muted transition-colors flex items-center justify-between ${sort === opt.value ? "text-primary font-medium" : ""}`}
                  >
                    {opt.label}
                    {sort === opt.value && <span className="text-primary">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setFilterOpen((o) => !o)}
            className={`flex items-center gap-1.5 text-xs font-medium border rounded-md px-3 py-1.5 transition-colors
              ${filterOpen || activeCount > 0
                ? "border-primary text-primary bg-primary/5"
                : "border-border hover:bg-muted"}`}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            Filters
            {activeCount > 0 && (
              <span className="bg-primary text-primary-foreground rounded-full w-4 h-4 text-[10px] flex items-center justify-center font-bold">
                {activeCount}
              </span>
            )}
            {filterOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        </div>
      </div>

      {/* Filter panel */}
      {filterOpen && (
        <div className="border-b border-border bg-muted/30 px-5 py-4 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {/* Player Count */}
            <FilterGroup title="Player Count">
              {PLAYER_COUNTS.map((pc) => (
                <CheckboxRow
                  key={pc}
                  label={pc === "7+" ? "7+ players" : `${pc} player${pc === "1" ? "" : "s"}`}
                  checked={filters.playerCounts.includes(pc)}
                  onChange={() => toggleCheck("playerCounts", pc)}
                />
              ))}
            </FilterGroup>

            {/* Play Time */}
            <FilterGroup title="Play Time">
              {PLAY_TIMES.map((pt) => (
                <CheckboxRow
                  key={pt.value}
                  label={pt.label}
                  checked={filters.playTimes.includes(pt.value)}
                  onChange={() => toggleCheck("playTimes", pt.value)}
                />
              ))}
            </FilterGroup>

            {/* Rating & Complexity */}
            <FilterGroup title="Rating & Complexity">
              <p className="text-xs text-muted-foreground -mt-1 mb-1">BGG Rating (1–10)</p>
              <div className="flex items-center gap-1.5">
                <Input
                  type="number" min="1" max="10" step="0.1"
                  placeholder="Min"
                  value={filters.minRating}
                  onChange={(e) => setFilters((f) => ({ ...f, minRating: e.target.value }))}
                  className="h-7 text-xs"
                />
                <span className="text-xs text-muted-foreground shrink-0">–</span>
                <Input
                  type="number" min="1" max="10" step="0.1"
                  placeholder="Max"
                  value={filters.maxRating}
                  onChange={(e) => setFilters((f) => ({ ...f, maxRating: e.target.value }))}
                  className="h-7 text-xs"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-3 mb-1">Complexity (1–5)</p>
              <div className="flex items-center gap-1.5">
                <Input
                  type="number" min="1" max="5" step="0.1"
                  placeholder="Min"
                  value={filters.minWeight}
                  onChange={(e) => setFilters((f) => ({ ...f, minWeight: e.target.value }))}
                  className="h-7 text-xs"
                />
                <span className="text-xs text-muted-foreground shrink-0">–</span>
                <Input
                  type="number" min="1" max="5" step="0.1"
                  placeholder="Max"
                  value={filters.maxWeight}
                  onChange={(e) => setFilters((f) => ({ ...f, maxWeight: e.target.value }))}
                  className="h-7 text-xs"
                />
              </div>
            </FilterGroup>

            {/* Availability — varies by mode */}
            {mode === "attendee" && (
              <FilterGroup title="Availability">
                <CheckboxRow
                  label="Only games being brought"
                  checked={filters.beingBrought}
                  onChange={() => setFilters((f) => ({ ...f, beingBrought: !f.beingBrought }))}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Show only games an attendee has marked as bringing.
                </p>
              </FilterGroup>
            )}
            {mode === "being_brought" && (
              <FilterGroup title="Availability">
                <CheckboxRow
                  label="Teacher available"
                  checked={filters.teacherAvailable ?? false}
                  onChange={() => setFilters((f) => ({ ...f, teacherAvailable: !f.teacherAvailable }))}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Only show games someone can teach tonight.
                </p>
              </FilterGroup>
            )}
          </div>

          {/* Mechanics & Categories row — only rendered when data exists */}
          {(mechanicOptions.length > 0 || categoryOptions.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 border-t border-border/50">
              {mechanicOptions.length > 0 && (
                <FilterGroup title="Mechanics">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {mechanicOptions.map((m) => (
                      <CheckboxRow
                        key={m.id}
                        label={m.name}
                        checked={filters.mechanics.includes(m.id)}
                        onChange={() =>
                          setFilters((f) => ({
                            ...f,
                            mechanics: f.mechanics.includes(m.id)
                              ? f.mechanics.filter((id) => id !== m.id)
                              : [...f.mechanics, m.id],
                          }))
                        }
                      />
                    ))}
                  </div>
                </FilterGroup>
              )}
              {categoryOptions.length > 0 && (
                <FilterGroup title="Categories">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {categoryOptions.map((c) => (
                      <CheckboxRow
                        key={c.id}
                        label={c.name}
                        checked={filters.categories.includes(c.id)}
                        onChange={() =>
                          setFilters((f) => ({
                            ...f,
                            categories: f.categories.includes(c.id)
                              ? f.categories.filter((id) => id !== c.id)
                              : [...f.categories, c.id],
                          }))
                        }
                      />
                    ))}
                  </div>
                </FilterGroup>
              )}
            </div>
          )}

          {activeCount > 0 && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Game grid */}
      <div className="p-5">
        {!hasBgg && mode === "library" ? (
          <BggEmptyState settingsPath={settingsPath} />
        ) : games.length === 0 ? (
          <EmptyGamesState mode={mode} />
        ) : processed.length === 0 ? (
          <NoResultsState onClear={clearFilters} />
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3">
            {processed.map((game) => {
              const id = game.bgg_id ?? game.bgg_game_id;
              const userOwns = mode === "mine" || myOwnedIds.has(id) || bringIds.has(id);
              return (
                <GameCard
                  key={id}
                  game={game}
                  userOwns={userOwns}
                  bringing={bringIds.has(id)}
                  wanting={wantToPlayIds.has(id)}
                  canTeach={canTeachIds.has(id)}
                  onBring={onBring ? () => onBring(id) : null}
                  onWantToPlay={onWantToPlay ? () => onWantToPlay(id) : null}
                  onSelect={() => setSelectedGame(game)}
                />
              );
            })}
          </div>
        )}
      </div>
      <GameDetailModal
        game={selectedGame}
        open={selectedGame != null}
        onClose={() => setSelectedGame(null)}
      />
    </div>
  );
}

// ─── Game cards ───────────────────────────────────────────────

function GameCard({ game, userOwns, bringing, wanting, canTeach, onBring, onWantToPlay, onSelect }) {
  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={onSelect}
        className="relative aspect-[2/3] rounded-lg overflow-hidden bg-muted group cursor-pointer text-left"
        title={`View details for ${game.title}`}
      >
        {game.thumbnail_url ? (
          <img
            src={game.thumbnail_url}
            alt={game.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Gamepad2 className="h-6 w-6 text-muted-foreground/40" />
          </div>
        )}

        {canTeach && (
          <div className="absolute top-1.5 left-1.5 z-10">
            <span className="inline-flex items-center gap-0.5 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
              <GraduationCap className="h-2.5 w-2.5" />
              Teach
            </span>
          </div>
        )}

        {game.bring_count > 0 && (
          <div
            className="absolute top-1.5 right-1.5 z-10"
            title={`${game.bring_count} ${game.bring_count === 1 ? "person is" : "people are"} bringing this`}
          >
            <span className="inline-flex items-center gap-0.5 bg-green-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
              <PackageCheck className="h-2.5 w-2.5" />
              {game.bring_count}
            </span>
          </div>
        )}

        {game.wtp_count > 0 && (
          <div
            className="absolute bottom-1.5 right-1.5 z-10"
            title={`${game.wtp_count} ${game.wtp_count === 1 ? "person wants" : "people want"} to play this`}
          >
            <span className="inline-flex items-center gap-0.5 bg-primary text-primary-foreground text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
              <Star className="h-2.5 w-2.5" />
              {game.wtp_count}
            </span>
          </div>
        )}
      </button>

      <div className="space-y-1">
        <p className="text-xs font-medium truncate leading-snug" title={game.title}>
          {game.title}
        </p>
        {game.bgg_rating != null && (
          <p className="text-[10px] text-muted-foreground">★ {Number(game.bgg_rating).toFixed(1)}</p>
        )}
      </div>

      {userOwns
        ? onBring && <BringButton bringing={bringing} onToggle={onBring} />
        : onWantToPlay && <WantToPlayButton wanting={wanting} onToggle={onWantToPlay} />
      }
    </div>
  );
}

function WantToPlayButton({ wanting, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full py-1.5 rounded text-[10px] font-medium leading-none flex items-center justify-center gap-1 transition-all
        ${wanting
          ? "bg-primary/15 text-primary ring-1 ring-primary/30"
          : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"}`}
    >
      <Star className="h-2.5 w-2.5 shrink-0" />
      {wanting ? "Want to play ✓" : "Want to play"}
    </button>
  );
}

function BringButton({ bringing, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full py-1.5 rounded text-[10px] font-medium leading-none flex items-center justify-center gap-1 transition-all
        ${bringing
          ? "bg-green-500/20 text-green-700 dark:text-green-400 ring-1 ring-green-500/30"
          : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"}`}
    >
      <PackageCheck className="h-2.5 w-2.5 shrink-0" />
      {bringing ? "Bringing ✓" : "Bringing?"}
    </button>
  );
}

// ─── Empty / loading states ───────────────────────────────────

function EmptyGamesState({ mode }) {
  const messages = {
    library:       { text: "No games in this list yet.", sub: "Sync your BGG library to populate your collection." },
    attendee:      { text: "No games have been added yet.", sub: "Attendees can mark games from their libraries once they join." },
    mine:          { text: "You haven't added any games yet.", sub: "Mark games from your library to bring to this event." },
    being_brought: { text: "No games are being brought yet.", sub: "Attendees can mark games from their libraries to bring." },
  };
  const { text, sub } = messages[mode] ?? { text: "No games yet.", sub: "" };
  return (
    <div className="py-10 flex flex-col items-center text-center">
      <Gamepad2 className="h-8 w-8 text-muted-foreground/40 mb-3" />
      <p className="text-sm font-medium text-foreground">{text}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1 max-w-xs">{sub}</p>}
    </div>
  );
}

function BggEmptyState({ settingsPath }) {
  return (
    <div className="py-8 flex flex-col items-center text-center">
      <Gamepad2 className="h-8 w-8 text-muted-foreground/50" />
      <p className="mt-3 text-sm font-medium">Link your BGG account</p>
      <p className="text-xs text-muted-foreground mt-1 max-w-xs">
        Add your BoardGameGeek username in Settings to sync your library.
      </p>
      {settingsPath && (
        <a href={settingsPath} className="mt-3 text-xs text-primary hover:underline">
          Go to Settings →
        </a>
      )}
    </div>
  );
}

function NoResultsState({ onClear }) {
  return (
    <div className="py-8 flex flex-col items-center text-center">
      <SlidersHorizontal className="h-8 w-8 text-muted-foreground/50" />
      <p className="mt-3 text-sm font-medium">No games match your filters</p>
      <button onClick={onClear} className="mt-2 text-xs text-primary hover:underline">
        Clear filters
      </button>
    </div>
  );
}

// ─── Shared primitives (also used by Home.jsx) ────────────────

export function FilterGroup({ title, children }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{title}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

export function CheckboxRow({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="rounded border-border accent-primary h-3.5 w-3.5"
      />
      <span className="text-xs group-hover:text-foreground text-muted-foreground transition-colors">
        {label}
      </span>
    </label>
  );
}
